import { useStore, type MiniMapNodeProps } from "@xyflow/react"
import {
  createElement,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
  type HTMLAttributes,
} from "react"
import {
  cloneMinimapSwatch,
  ensureMinimapSwatch,
  flowMinimapMediaKey,
  getMinimapSwatch,
  retainMinimapSwatches,
  subscribeMinimapSwatch,
} from "@/lib/flow-minimap-swatch"
import type { FlowNodeRow } from "@/types/flow"

function serverSwatch(): HTMLCanvasElement | null {
  return null
}

function useMinimapSwatch(key: string | null): HTMLCanvasElement | null {
  const subscribe = useCallback(
    (onStoreChange: () => void) => (key ? subscribeMinimapSwatch(key, onStoreChange) : () => undefined),
    [key],
  )
  const getSnapshot = useCallback(() => (key ? getMinimapSwatch(key) : null), [key])
  return useSyncExternalStore(subscribe, getSnapshot, serverSwatch)
}

function FlowMinimapNodeInner({ id, x, y, width, height, borderRadius, color }: MiniMapNodeProps) {
  const mediaKey = useStore((state) => {
    const data = state.nodeLookup.get(id)?.data as FlowNodeRow | undefined
    if (!data) return null
    return flowMinimapMediaKey(data)
  })
  const master = useMinimapSwatch(mediaKey)
  const hostRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const paintedMasterRef = useRef<HTMLCanvasElement | null>(null)
  const clipId = `flow-minimap-clip-${id}`

  useEffect(() => {
    const release = retainMinimapSwatches()
    if (mediaKey) ensureMinimapSwatch(id, mediaKey)
    return release
  }, [id, mediaKey])

  // React затирает императивный canvas при любом коммите пустого div.
  // Без массива зависимостей эффект возвращает его после каждого такого коммита.
  // Панорама доски компонент не перерисовывает: memo, x/y миникарты те же.
  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host || !master) return
    if (paintedMasterRef.current !== master || !canvasRef.current) {
      const canvas = cloneMinimapSwatch(master)
      canvas.style.width = "100%"
      canvas.style.height = "100%"
      canvas.style.display = "block"
      canvasRef.current = canvas
      paintedMasterRef.current = master
    }
    const canvas = canvasRef.current
    if (canvas && canvas.parentElement !== host) host.replaceChildren(canvas)
  })

  return (
    <g>
      <clipPath id={clipId}>
        <rect x={x} y={y} width={width} height={height} rx={borderRadius} ry={borderRadius} />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        <rect x={x} y={y} width={width} height={height} rx={borderRadius} ry={borderRadius} style={{ fill: color }} />
        {master && width > 0 && height > 0 ? (
          <foreignObject x={x} y={y} width={width} height={height} pointerEvents="none">
            {/* xmlns оставляет div в HTML-пространстве внутри SVG. В типах React его нет. */}
            {createElement("div", {
              ref: hostRef,
              xmlns: "http://www.w3.org/1999/xhtml",
              style: { width: "100%", height: "100%", overflow: "hidden", pointerEvents: "none" },
            } as HTMLAttributes<HTMLDivElement>)}
          </foreignObject>
        ) : null}
      </g>
    </g>
  )
}

export const FlowMinimapNode = memo(FlowMinimapNodeInner)
